/**
 * #250 one Worker's AI offering choice - Auto or an exact pin - for the Settings > AI Workforce
 * control and the same control on the Worker card. Both send set_worker_offering through
 * useWorkerOffering; this only derives what the selector shows and whether it can change now.
 *
 * Rules kept from the AI Workforce control: a pin is exact, a pin no longer offered on the
 * computer stays visible as unavailable (never swapped), and a Worker with a run in progress
 * cannot switch until that run finishes or is stopped (set_worker_offering refuses it).
 */
import type { ModelOfferingRef, NodeOffering, NodePresence, RuntimeProof, Worker, WorkerOfferingPreference, WorkforceSession } from './data';
import { workforceSessionView } from './workforce-display';

export const WORKER_OFFERING_WAIT_NOTE = 'finish or stop the current run to switch';

export function workerOfferingLabel(key: string, offerings: readonly ModelOfferingRef[]): string {
  const row = offerings.find((item) => item.key === key);
  return row ? `${row.runtime_key} · ${row.model_key} · ${row.execution_location}` : key;
}

export function workerOfferingProof(
  key: string, nodeId: string, offerings: readonly ModelOfferingRef[], proofs: readonly RuntimeProof[],
): RuntimeProof | null {
  const runtime = offerings.find((item) => item.key === key)?.runtime_key;
  return runtime ? proofs.find((item) => item.node_id === nodeId && item.runtime_key === runtime) ?? null : null;
}

export function workerOfferingProofLabel(proof: RuntimeProof | null): string {
  if (proof === null || proof.run_count === 0) return 'unproven';
  if (proof.proven) return `proven · ${proof.success_count} success${proof.success_count === 1 ? '' : 'es'}`;
  return `failed · ${proof.failure_count} attempt${proof.failure_count === 1 ? '' : 's'}`;
}

export interface WorkerOfferingOption {
  readonly key: string;
  readonly label: string;
  readonly available: boolean;
}

export interface WorkerOfferingChoice {
  readonly workerId: string;
  readonly workerNumber: number;
  readonly nodeId: string;
  readonly session: WorkforceSession;
  readonly node: NodePresence | null;
  readonly offline: boolean;
  /** The Worker pin on this computer; null is Auto. */
  readonly preferenceKey: string | null;
  /** Offerings on this computer, sorted by label. */
  readonly available: readonly string[];
  /** What the selector lists: an unavailable pin first, then the computer's offerings. */
  readonly options: readonly WorkerOfferingOption[];
  /** A run is in progress: the selection waits until it finishes or is stopped. */
  readonly working: boolean;
}

export function workerOfferingChoice(input: {
  readonly worker: Pick<Worker, 'id' | 'number' | 'status'>;
  readonly sessions: readonly WorkforceSession[];
  readonly nodes: readonly NodePresence[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
  readonly preferences: readonly WorkerOfferingPreference[];
  readonly runtimeProofs: readonly RuntimeProof[];
  readonly nowMs: number;
}): WorkerOfferingChoice | null {
  const view = workforceSessionView(input.worker.id, input.sessions, input.nodes, input.nowMs);
  if (view === null) return null;
  const { session, node, offline } = view;
  const preferenceKey = input.preferences
    .find((item) => item.worker_id === input.worker.id && item.node_id === session.node_id)?.offering_key ?? null;
  const available = input.nodeOfferings.filter((item) => item.node_id === session.node_id).map((item) => item.offering_key)
    .sort((a, b) => workerOfferingLabel(a, input.modelOfferings).localeCompare(workerOfferingLabel(b, input.modelOfferings)));
  const keys = preferenceKey !== null && !available.includes(preferenceKey) ? [preferenceKey, ...available] : available;
  const options = keys.map((key) => ({
    key,
    available: available.includes(key),
    label: `${workerOfferingLabel(key, input.modelOfferings)}${available.includes(key)
      ? ` — ${workerOfferingProofLabel(workerOfferingProof(key, session.node_id, input.modelOfferings, input.runtimeProofs))}`
      : ' — unavailable on this computer'}`,
  }));
  return {
    workerId: input.worker.id,
    workerNumber: input.worker.number,
    nodeId: session.node_id,
    session,
    node,
    offline,
    preferenceKey,
    available,
    options,
    working: !offline && (input.worker.status === 'working' || session.status !== 'idle'),
  };
}
